import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { ClientsPage } from "./pages/Clients";
import { ToolsPage } from "./pages/Tools";
import { UsersPage } from "./pages/Users";
import {
  arrivedFromMyApps,
  clearResumeAttempt,
  displayName,
  groupsOf,
  hasAnyRole,
  isAdmin,
  isNoSessionError,
  markResumeAttempted,
  MICROSOFT_PROVIDER,
  rememberReturnTo,
  resumeAttempted,
} from "./lib/auth";
import { hostedLogoutUrl, nonProductionLabel } from "./lib/config";

/**
 * Pick the session back up after a refresh, without asking for anything.
 *
 * Tokens are held in memory on purpose, so every refresh starts with none --
 * see the note in lib/config. That is worth keeping, but it should not cost a
 * preparer a click: Cognito still holds its own session cookie, so asking for
 * tokens with `prompt=none` gets them back with no login form and nothing to
 * press. If the hosted session has really gone, Cognito says so and we show
 * the sign-in button instead.
 *
 * Tried once per tab. The flag is cleared on success, so the next refresh
 * tries again, and a failure does not turn into a redirect loop.
 */
function useResumedSession(): boolean {
  const auth = useAuth();
  // Any error at all stops the attempt. A misconfigured client would
  // otherwise redirect, fail, redirect again and lock the app in a loop --
  // which is worse than the click this replaces.
  const resuming =
    !auth.isAuthenticated && !auth.isLoading && !auth.error &&
    !resumeAttempted();

  useEffect(() => {
    if (!resuming || auth.activeNavigator) return;
    markResumeAttempted();
    // From the My Apps tile: straight to Microsoft, which already has a
    // session, so nothing is shown. Same once-per-tab guard as a resume.
    //
    // The state carries the address this visit started at, so a deep link --
    // or the Xero callback's ?xero=… outcome -- survives the round trip.
    void auth.signinRedirect({
      state: rememberReturnTo(),
      extraQueryParams: arrivedFromMyApps()
        ? { identity_provider: MICROSOFT_PROVIDER }
        : { prompt: "none" },
    });
  }, [resuming, auth]);

  useEffect(() => {
    if (auth.isAuthenticated) clearResumeAttempt();
  }, [auth.isAuthenticated]);

  return resuming;
}

/** Two pages, so this is a tab rather than a router.
 *
 *  Adding react-router would also mean deciding what /staff does on a hard
 *  refresh, which currently works only because CloudFront rewrites unknown
 *  paths to index.html for the OIDC callback. Not worth it for one more page. */
type Tab = "clients" | "tools" | "staff";

export function App() {
  const auth = useAuth();
  const resuming = useResumedSession();

  if (auth.isLoading || resuming) {
    return <Shell><p className="muted">Signing in…</p></Shell>;
  }

  // Cognito answering "no session" to a promptless request is the expected
  // outcome when it has expired, not a failure to report. Showing the error
  // screen for it would make an ordinary first visit look broken.
  if (auth.error && !isNoSessionError(auth.error)) {
    return (
      <Shell>
        <div className="notice error">
          <strong>Could not sign in.</strong>
          <p>{auth.error.message}</p>
          <button onClick={() => void auth.signinRedirect({ state: rememberReturnTo() })}>
            Try again
          </button>
        </div>
      </Shell>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Shell>
        <div className="signin">
          <p>Sign in with your Casa Advisory account.</p>
          <button
            className="primary"
            onClick={() => void auth.signinRedirect({ state: rememberReturnTo() })}
          >
            Sign in
          </button>
        </div>
      </Shell>
    );
  }

  // An account with no group can reach nothing. Saying so beats an empty page
  // followed by a 403 from every request.
  if (!hasAnyRole(auth.user)) {
    return (
      <Shell user={displayName(auth.user)}>
        <div className="notice error">
          <strong>No role assigned.</strong>
          <p>
            Your account exists but has no role yet. An administrator needs to add
            you to Preparer, Reviewer, Director Approval or Admin.
          </p>
        </div>
      </Shell>
    );
  }

  return <SignedIn />;
}

function SignedIn() {
  const auth = useAuth();
  const [tab, setTab] = useState<Tab>("clients");
  const admin = isAdmin(auth.user);

  return (
    <Shell
      user={displayName(auth.user)}
      roles={groupsOf(auth.user)}
      // Staff management is Admin-only and enforced server-side; hiding the
      // tab is presentation, not the control.
      tabs={{ current: tab, onChange: setTab, admin }}
    >
      {admin && tab === "staff" ? <UsersPage /> : tab === "tools" ? <ToolsPage /> : <ClientsPage />}
    </Shell>
  );
}

function Shell(props: {
  children: React.ReactNode;
  user?: string;
  roles?: string[];
  tabs?: { current: Tab; onChange: (tab: Tab) => void; admin: boolean };
}) {
  const environment = nonProductionLabel();
  return (
    <>
      {environment && (
        <div className="env-bar">
          {environment} environment &mdash; holds real client data, but is not
          the production system
        </div>
      )}
      <header>
        <div>
          <h1>
            Casa Advisory
            {environment && <span className="env">{environment}</span>}
          </h1>
          <p className="muted">BAS Workpapers</p>
        </div>
        {props.user && (
          <div className="session">
            <span>{props.user}</span>
            {props.roles && props.roles.length > 0 && (
              <span className="roles">{props.roles.join(" · ")}</span>
            )}
            <a href={hostedLogoutUrl()}>Sign out</a>
          </div>
        )}
      </header>
      {props.tabs && (
        <nav className="tabs">
          {([
            ["clients", "Clients"],
            ["tools", "Tools"],
            ["staff", "Staff"],
          ] as const)
            .filter(([value]) => value !== "staff" || props.tabs?.admin)
            .map(([value, text]) => (
            <button
              key={value}
              className={props.tabs?.current === value ? "tab current" : "tab"}
              aria-current={props.tabs?.current === value ? "page" : undefined}
              onClick={() => props.tabs?.onChange(value)}
            >
              {text}
            </button>
          ))}
        </nav>
      )}
      <main>{props.children}</main>
    </>
  );
}
